import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMerchantQuery, useGetRefundReserveQuery } from 'services/merchants';
import { useGetRefundWalletTransactionsQuery } from 'services/refunds';

import useGetUuid from 'components/accountSettings/utils';

import { ReduxToolkitError } from 'types/http';

import { usePersistentFilters } from 'utils/filters';
import { decoratePagination, getDefaultPagination, undecoratePagination } from 'utils/pagination';
import { usePersistentPagination } from 'utils/tables/pagination';
import { useTableSorting } from 'utils/tables/sorting';

import { KEYS, LABELS } from './keys';
import RefundAccount from './refundAccount';
import { Filters } from './types';
import { RefundAccountTransactionRow, makeFilters, prepareFilters, transformTransactionsListDataToTableFormat } from './utils';

const DEFAULT_REFUND_SETTLEMENT_CURRENCY_CODE = 'USDC';

const RefundAccountContainer = () => {
  const { t } = useTranslation();
  const { filters, updateFilters, clearFilters } = usePersistentFilters<Filters>({}, KEYS.FILTER_KEY);
  const { sorting, onSort } = useTableSorting({
    defaultSorting: {
      direction: KEYS.DEFAULT_TABLE_SORTING.DIRECTION,
      field: KEYS.DEFAULT_TABLE_SORTING.FIELD,
    },
  });

  const { pagination, setPagination, changeRowsPerPage, changePage } = usePersistentPagination(
    getDefaultPagination(),
    [filters, sorting],
    KEYS.FILTER_KEY,
  );
  const merchantUuid = useGetUuid() ?? '';

  const { data, isLoading, error } = useGetRefundWalletTransactionsQuery([
    merchantUuid,
    { pagination: undecoratePagination(pagination), filters: prepareFilters(filters) },
  ]);

  const { data: merchant } = useGetMerchantQuery(merchantUuid!, {
    skip: !merchantUuid,
  });
  const { data: refundReserve } = useGetRefundReserveQuery(merchantUuid, {
    skip: !merchantUuid,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data?.pagination) {
      setPagination(decoratePagination(data?.pagination));
    }
  }, [data?.pagination]);

  const settlementCurrencyCode = refundReserve?.settlementCurrencyCode ?? DEFAULT_REFUND_SETTLEMENT_CURRENCY_CODE;
  const fiatCurrencyCode = refundReserve?.fiatCurrencyCode ?? merchant?.data?.mainFiatCurrency;
  const custodialWalletTransactions: RefundAccountTransactionRow[] = transformTransactionsListDataToTableFormat(data?.data ?? [], {
    settlementCurrencyCode,
  });

  const filterSettings = makeFilters();

  const getTransactionsErrorMessage = () => {
    if (!error) {
      return undefined;
    }

    return (error as ReduxToolkitError)?.data?.errorMessage ?? t(LABELS.FETCH_TRANSACTIONS_ERROR);
  };

  return (
    <RefundAccount
      refundAccountTransactions={custodialWalletTransactions}
      refundWalletBalance={refundReserve?.reserveBalance ?? merchant?.data?.refundWalletBalance}
      settlementCurrencyCode={settlementCurrencyCode}
      fiatCurrencyCode={fiatCurrencyCode}
      displayFxRate={refundReserve?.displayFxRate}
      error={getTransactionsErrorMessage()}
      isLoading={isLoading}
      pagination={pagination}
      onSort={onSort}
      sorting={sorting}
      onChangePage={changePage}
      onChangeRowsPerPage={changeRowsPerPage}
      filterProps={{
        filterSettings,
        onFilterChange: updateFilters,
        onFiltersClear: clearFilters,
        filters,
      }}
    />
  );
};

export default RefundAccountContainer;
